import React, { useEffect, useMemo } from 'react';

import { Route, Routes, useParams } from 'react-router-dom';

import useUsers from 'api/useUsers';
import { User } from 'common/contracts';
import { ELocalStorageKeys } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { localStorageUtil } from 'utils/localStorageUtil';

import { bundleDesigns } from 'components/TemplatesStudio/DesignsMetadata/bundles.metadata';
import { promotionDesigns } from 'components/TemplatesStudio/DesignsMetadata/promotions.metadata';
import { rollingOffersDesigns } from 'components/TemplatesStudio/DesignsMetadata/rollingOffers.metadata';

import { EThemes } from '../enums';
import { getDefaultBodyBlocksOrder, isDesignNew } from '../utils';

import { TemplateStudioForm } from './FormPage/Form';
import { TemplatesStudioMainPage } from './MainPage/Main';

const formatDateMMYY = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = String(date.getFullYear()).slice(-2);
  return `${month}-${year}`;
};

export const TemplatesStudioView: React.FC = () => {
  const {
    setCurrentCompany,
    setDefaultBodyBlocksOrder,
    setSupportedThemes,
    setRelevantBundleDesigns,
    setRelevantPromotionDesigns,
    setRelevantRollingOffersDesigns,
    supportedThemes
  } = useTemplateStudioState();
  const { publisherId } = useParams();
  const { fetchFeatureFlags } = useUsers({ publisherId });
  const featureFlags = useMemo(
    () => fetchFeatureFlags.data?.featureFlags,
    [fetchFeatureFlags.data?.featureFlags]
  );

  useEffect(() => {
    if (localStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)) return;

    localStorageUtil.set(ELocalStorageKeys.PLAYER_DATA, {
      playerId: '123456',
      playerName: 'Player Name'
    });
  }, []);

  const userDetails = localStorageUtil.getAny<User>(
    ELocalStorageKeys.USER_DETAILS
  );

  const projectThemes = useMemo(
    () =>
      userDetails?.projects?.find(
        (project) => project.publisherId === publisherId
      )?.themes || [EThemes.ROYAL_BLAST],
    [userDetails?.projects]
  );

  useEffect(() => {
    setSupportedThemes(projectThemes);

    return () => {
      setSupportedThemes([]);
    };
  }, [projectThemes.length]);

  useEffect(() => {
    if (!supportedThemes || supportedThemes.length === 0) return;
    const relevantBundleDesigns = Object.values(bundleDesigns).filter(
      ({ theme }) => supportedThemes!.includes(theme)
    );

    setRelevantBundleDesigns(
      relevantBundleDesigns.map((design) => {
        const isNew = isDesignNew(design.createdAt);
        return {
          ...design,
          isNew,
          name: isNew
            ? `${design.name} (${formatDateMMYY(design.createdAt)})`
            : design.name
        };
      })
    );

    const relevantPromotionDesigns = Object.values(promotionDesigns).filter(
      ({ theme }) => supportedThemes!.includes(theme)
    );

    setRelevantPromotionDesigns(
      relevantPromotionDesigns.map((design) => {
        const isNew = isDesignNew(design.createdAt);
        return {
          ...design,
          isNew,
          name: isNew
            ? `${design.name} (${formatDateMMYY(design.createdAt)})`
            : design.name
        };
      })
    );

    const relevantRollingOffersDesigns = Object.values(
      rollingOffersDesigns
    ).filter(({ theme }) => supportedThemes!.includes(theme));

    setRelevantRollingOffersDesigns(
      relevantRollingOffersDesigns.map((design) => {
        const isNew = isDesignNew(design.createdAt);
        return {
          ...design,
          isNew,
          name: isNew
            ? `${design.name} (${formatDateMMYY(design.createdAt)})`
            : design.name
        };
      })
    );
  }, [supportedThemes?.length]);

  useEffect(() => {
    const userDetails = localStorageUtil.getAny<User>(
      ELocalStorageKeys.USER_DETAILS
    );
    if (userDetails?.company) {
      setCurrentCompany(userDetails.company);
    }

    return () => {
      setCurrentCompany(undefined);
    };
  }, []);

  useEffect(() => {
    if (featureFlags) {
      setDefaultBodyBlocksOrder(getDefaultBodyBlocksOrder(featureFlags));
    }
  }, [featureFlags]);

  return (
    <Routes>
      <Route path="/" element={<TemplatesStudioMainPage />} />
      <Route path="/form" element={<TemplateStudioForm />} />
      <Route
        path="/form/:pageId"
        element={<TemplateStudioForm edit={true} />}
      />
    </Routes>
  );
};
